import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { beaconOptionLabel, chargeBadge, currentActor } from '../utils/beacons';
import { lotStatusLabel } from '../features/materials/lots';
import {
  shipmentPallets, loadPalletBeacons, planPallets, autoFill, chooseBeacon, pendingChanges,
  palletsWithoutBeacon, assignPallets,
} from '../features/materials/shipmentBeacons';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function optionLabel(b, rows, mtnId) {
  const bits = [beaconOptionLabel(b)];
  if (b.battery_state === 'critical') bits.push('charge now');
  else if (b.battery_state === 'low') bits.push('needs charging');
  const other = rows.find((r) => r.mtnId !== mtnId && r.choice === b.beacon_id);
  if (other) bits.push(`picked for TRK ${other.trk}`);
  return bits.join(' · ');
}

/**
 * Pallet beacons for one PO receipt (shipment): every pallet (TRK) of the receipt with its beacon,
 * and a free beacon already picked for each pallet that has none (features/materials/shipmentBeacons
 * autoFill: in range first, then the most charge). Any line can be changed before Assign; Assign
 * puts them all on. Opened from the PO page's Shipments History (the Beacons button), only when
 * GET /features says pallet_beacons is on.
 */
export default function ShipmentBeaconsModal({ shipment, poLines, poNumber, onClose, onChanged }) {
  // The receipt as it was when the popup opened: a PO line edit behind it must not reset the picks.
  const [pallets] = useState(() => shipmentPallets(shipment, poLines));
  const [rows, setRows] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState({});
  const [summary, setSummary] = useState(null); // {tone, text}

  const load = useCallback(async () => {
    setLoading(true);
    const { lots, available: free } = await loadPalletBeacons(pallets.map((p) => p.mtnId));
    setAvailable(free);
    setRows(planPallets(pallets, lots, free));
    setLoading(false);
  }, [pallets]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const changes = pendingChanges(rows);
  const uncovered = palletsWithoutBeacon(rows);
  const openCount = rows.filter((r) => r.status === 'open').length;

  const handleAssign = async () => {
    if (!changes.length) return;
    setBusy(true);
    setSummary(null);
    setResults({});
    try {
      const out = await assignPallets(changes, {
        actor: currentActor(),
        onProgress: (i, n) => setProgress(`Assigning ${i} of ${n}…`),
      });
      setResults(out.results);
      if (out.failed) {
        setSummary({
          tone: 'danger',
          text: `${plural(out.assigned, 'beacon', 'beacons')} assigned, ${out.failed} didn't: see the line.`,
        });
      } else if (out.assigned) {
        setSummary({
          tone: 'success',
          text: `${plural(out.assigned, 'beacon', 'beacons')} assigned. Each screen shows its TRK within a few seconds.`,
        });
      }
      await load();
      if (out.assigned && onChanged) onChanged();
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const nowCell = (r) => {
    if (r.status === 'has') {
      const badge = chargeBadge(r.beacon);
      return (
        <>
          <span className="badge bg-primary font-monospace" data-testid={`beacon-now-${r.mtnId}`}>
            <i className="bi bi-broadcast me-1"></i>{r.current}
          </span>
          {badge && <span className={`badge ms-1 ${badge.className}`}>{badge.text}</span>}
        </>
      );
    }
    if (r.status === 'done') {
      return <span className="small text-muted">{lotStatusLabel(r.lotStatus)}: no beacon needed</span>;
    }
    if (r.status === 'error') return <span className="small text-danger">{r.message}</span>;
    return <span className="text-muted">—</span>;
  };

  const pickCell = (r) => {
    if (r.status !== 'open' && r.status !== 'has') return null;
    const res = results[r.mtnId];
    return (
      <>
        <select
          className={`form-select form-select-sm${r.choice && r.choice !== r.current ? ' border-primary' : ''}`}
          style={{ minWidth: 220 }}
          value={r.choice}
          onChange={(e) => setRows(chooseBeacon(rows, r.mtnId, e.target.value, available))}
          aria-label={`Beacon for TRK ${r.trk}`}
          disabled={busy}
        >
          {r.status === 'has'
            ? <option value={r.current}>Keep {r.current}</option>
            : <option value="">No beacon</option>}
          {available.map((b) => (
            <option key={b.beacon_id} value={b.beacon_id}>{optionLabel(b, rows, r.mtnId)}</option>
          ))}
        </select>
        {res && (
          <div className={`small mt-1 text-${res.ok ? 'success' : 'danger'}`} data-testid={`beacon-result-${r.mtnId}`}>
            {res.ok ? `${res.beaconId} ${res.moved ? 'moved here' : 'is on it'}.` : res.message}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog"
      aria-label={`Beacons for shipment ${shipment.shipment_id}`}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-broadcast me-2"></i>Pallet beacons: shipment {shipment.shipment_id}
              {poNumber ? <span className="text-muted fs-6 ms-2">PO {poNumber} · {shipment.shipment_date}</span> : null}
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={busy}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-2">
              Each pallet without a beacon already has a free one picked (in range first, then the most
              charge). Change any line, then <strong>Assign</strong>. Each beacon&apos;s screen shows its TRK
              within a few seconds: put it on the pallet whose label matches.
            </p>

            {loading && rows.length === 0 && <div className="small text-muted">Loading beacons…</div>}

            {!loading && pallets.length === 0 && (
              <div className="small text-muted">This shipment has no material pallets (no TRK numbers).</div>
            )}

            {rows.length > 0 && (
              <div className="table-responsive">
              <table className="table table-sm align-middle mb-2">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>TRK</th>
                    <th>Material</th>
                    <th style={{ width: 120 }}>Heat #</th>
                    <th style={{ width: 60 }}>Qty</th>
                    <th style={{ width: 170 }}>Beacon now</th>
                    <th style={{ width: 340 }}>Assign</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.mtnId} data-testid={`pallet-row-${r.mtnId}`}>
                      <td className="font-monospace fw-bold">{r.trk}</td>
                      <td>
                        <div>{r.code}</div>
                        {r.description && <div className="small text-muted">{r.description}</div>}
                      </td>
                      <td>{r.heat || '—'}</td>
                      <td>{r.quantity ?? '—'}</td>
                      <td>{nowCell(r)}</td>
                      <td>{pickCell(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            {!loading && openCount > 0 && available.length === 0 && (
              <div className="small text-muted">
                No free beacons. <Link to="/beacons" onClick={onClose}>Add one on the Beacons page</Link>, or
                switch one on near a gateway.
              </div>
            )}
            {!loading && uncovered > 0 && available.length > 0 && (
              <div className="small text-warning-emphasis" data-testid="beacons-short">
                {plural(uncovered, 'pallet has', 'pallets have')} no free beacon left to pick.
              </div>
            )}
          </div>
          <div className="modal-footer justify-content-between">
            <div className="small">
              {progress && <span className="text-muted">{progress}</span>}
              {!progress && summary && <span className={`text-${summary.tone}`} role="status">{summary.text}</span>}
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setRows(autoFill(rows, available))}
                disabled={busy || loading || rows.length === 0}
                title="Pick free beacons for every pallet again"
              >
                Auto-fill
              </button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={onClose} disabled={busy}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleAssign}
                disabled={busy || loading || changes.length === 0}
              >
                {changes.length === 0 ? 'Assign' : `Assign ${plural(changes.length, 'beacon', 'beacons')}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
